import { zodResolver } from '@hookform/resolvers/zod';
import { UserMinus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { STUDY_MEMBERSHIP_ROLES } from '@/clinical-study/vocabulary';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';
import { useKeyedPendingWrite, usePendingWrite } from '@/lib/use-pending-write';

/** The label of each membership role, in the rows and the select alike. */
const ROLE_LABEL_KEYS = {
  investigator: 'memberships.roleInvestigator',
  assistant_coordinator: 'memberships.roleAssistant',
  data_manager: 'memberships.roleDataManager',
} as const;

type StudyMembership =
  paths['/v1/clinical-study/studies/{studyId}/memberships']['get']['responses'][200]['content']['application/json'][number];

type RosterState = { status: 'loading' | 'error' | 'ready'; memberships: StudyMembership[] };

// SL-0045 Task 6.3: a real Identity Platform UID is a NON-UUID string — the prior `.uuid()`
// bound (and its "Deve essere un UUID valido" message) instructed the user to enter a value that
// is never a UUID. Mirrors the server bound exactly; the server-side assignee-existence read is
// the real gate.
const assignMembershipSchema = z.object({
  userId: z.string().trim().min(1),
  role: z.enum(STUDY_MEMBERSHIP_ROLES),
});
type AssignMembershipForm = z.infer<typeof assignMembershipSchema>;

/**
 * `StudyMembershipsSection` (FR-9.3/FR-9.5): Investigator/Assistant-Coordinator assignment for a
 * study, mirroring `RolesSection.tsx`'s raw-UUID grant-form + revoke-with-confirm + `DataTable`
 * shape closely — adapted to the two-value closed `role` `Select` (a genuine `Controller`+
 * `Select`+`z.enum` closed vocabulary over `STUDY_MEMBERSHIP_ROLES`, never free text).
 */
export function StudyMembershipsSection({ studyId }: { studyId: string }) {
  const { t } = useTranslation(['clinical-study', 'common']);
  const [state, setState] = useState<RosterState>({ status: 'loading', memberships: [] });
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AssignMembershipForm>({
    resolver: zodResolver(assignMembershipSchema),
    defaultValues: { role: 'investigator' },
  });
  const { pending, run } = usePendingWrite();
  const { isPending, run: runRow } = useKeyedPendingWrite();

  useEffect(() => {
    setState({ status: 'loading', memberships: [] });
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}/memberships', {
        params: { path: { studyId } },
      })
      .then(({ data, error, response }) => {
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error', memberships: [] });
          return;
        }
        setState({ status: 'ready', memberships: data });
      })
      .catch(() => setState({ status: 'error', memberships: [] }));
  }, [studyId]);

  const onAssign = async (values: AssignMembershipForm) => {
    setDuplicateError(null);
    setAssignError(null);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/memberships',
      {
        params: { path: { studyId } },
        body: { userId: values.userId, role: values.role },
      },
    );

    if (error !== undefined || !response.ok || data === undefined) {
      if (response.status === 409) {
        setDuplicateError(error?.detail ?? t('memberships.duplicateError'));
      } else {
        setAssignError(error?.detail ?? t('writeError'));
      }
      return;
    }

    reset({ role: values.role });
    setState((previous) => ({
      status: 'ready',
      memberships: [data, ...previous.memberships],
    }));
    toast.success(t('memberships.assignedToast'));
  };

  const onRevoke = async (membership: StudyMembership) => {
    if (!window.confirm(t('memberships.revokeConfirm'))) {
      return;
    }

    setRevokeError(null);
    const { error, response } = await apiClient.DELETE(
      '/v1/clinical-study/studies/{studyId}/memberships/{membershipId}',
      { params: { path: { studyId, membershipId: membership.id } } },
    );

    if (error !== undefined || !response.ok) {
      setRevokeError(
        error?.detail ??
          (response.status === 404 ? t('memberships.notFoundError') : t('writeError')),
      );
      return;
    }

    setState((previous) => ({
      ...previous,
      memberships: previous.memberships.filter((row) => row.id !== membership.id),
    }));
    toast.success(t('memberships.revokedToast'));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('memberships.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <form
          className="space-y-4"
          onSubmit={(event) => void handleSubmit((values) => run(() => onAssign(values)))(event)}
        >
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="membership-user-id">
              {t('memberships.userIdLabel')}
            </label>
            <Input id="membership-user-id" {...register('userId')} />
            {errors.userId && <p className="text-destructive text-sm">{errors.userId.message}</p>}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="membership-role">
              {t('memberships.roleLabel')}
            </label>
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="membership-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="investigator">
                      {t('memberships.roleInvestigator')}
                    </SelectItem>
                    <SelectItem value="assistant_coordinator">
                      {t('memberships.roleAssistant')}
                    </SelectItem>
                    <SelectItem value="data_manager">{t('memberships.roleDataManager')}</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          {duplicateError && <p className="text-destructive text-sm">{duplicateError}</p>}
          {assignError && <p className="text-destructive text-sm">{assignError}</p>}
          <Button type="submit" disabled={pending}>
            {t('memberships.assignSubmit')}
          </Button>
        </form>

        {revokeError && <p className="text-destructive text-sm">{revokeError}</p>}

        {state.status === 'error' && (
          <p className="text-destructive text-sm">{t('memberships.loadError')}</p>
        )}
        {state.status === 'loading' && <TableSkeleton columns={4} />}
        {state.status === 'ready' && (
          <DataTable
            columns={[
              {
                id: 'user',
                header: t('memberships.userColumnLabel'),
                cell: ({ row }) => <code>{row.original.userId}</code>,
              },
              {
                id: 'role',
                header: t('memberships.roleColumnLabel'),
                cell: ({ row }) => t(ROLE_LABEL_KEYS[row.original.role]),
              },
              {
                accessorKey: 'assignedAt',
                header: t('memberships.assignedAtColumnLabel'),
                cell: ({ getValue }) => formatDate(getValue<string>()),
              },
              {
                id: 'actions',
                header: t('memberships.actionsColumnLabel'),
                /*
                 * NO BULK BAR AND THE CONFIRMATION STAYS, unlike the platform-administration
                 * tables. § Undoable deletion's derogation names three core-api writes and this is
                 * not one of them, and the standing exception above it covers this module on the
                 * opposite ground — clinical-study writes are audited and append-only, so there is
                 * no undo model here to lean on either. With neither an undo nor a named
                 * derogation, the conservative reading is the one that keeps the prompt.
                 */
                cell: ({ row }) => (
                  <RowActionsMenu
                    label={t('common:rowActionsFor', { name: row.original.userId })}
                    actions={[
                      {
                        id: 'revoke',
                        label: t('memberships.revokeButton'),
                        icon: UserMinus,
                        tone: 'destructive',
                        busy: isPending(row.original.id),
                        onSelect: () => void runRow(row.original.id, () => onRevoke(row.original)),
                      },
                    ]}
                  />
                ),
              },
            ]}
            data={state.memberships}
            emptyMessage={t('memberships.emptyMessage')}
          />
        )}
      </CardContent>
    </Card>
  );
}
